import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import { DataTable } from '@components/tables'
import { useAppDispatch } from '@hooks/reduxHooks'
import {
  Box,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { QuestionDto, Site } from '@services/api'
import { Actions } from '@state/actions'
import { useSelector } from '@state/store'
import {
  bulkDeleteQuestions,
  deleteQuestion,
  findAllQuestions,
} from '@state/thunks/questionThunk'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import { EditIcon, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { removeSites } from '../../../state/reducers/siteReducer'
import { QuestionForm } from './QuestionForm'

export default function Questions() {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState<
    QuestionDto | undefined
  >(undefined)

  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [selectedSiteId, setSelectedSiteId] = useState<number | ''>('')

  const { loading, questions } = useSelector(({ question, loading }) => ({
    questions: question.questions,
    loading: loading[Actions.ROLE_FIND_ALL] || loading[Actions.ROLE_UPDATE],
  }))

  const user = useSelector(({ auth }) => auth.user)
  const sites = useMemo(
    () => (user as unknown as { sites: Site[] })?.sites || [],
    [user],
  )

  const handleOpen = () => {
    setSelectedQuestion(undefined)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSelectedQuestion(undefined)
  }

  const handleEdit = (question: QuestionDto) => {
    setSelectedQuestion(question)
    setOpen(true)
  }

  const columns: GridColumns = [
    { field: 'question', headerName: 'Question', flex: 1 },
    { field: 'type', headerName: 'Question Type', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'right',
      width: 120,
      renderCell: ({ row }) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(row)}>
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              setSelectedQuestion(row)
              setDeleteDialogOpen(true)
            }}
          >
            <Trash2 />
          </IconButton>
        </Box>
      ),
    },
  ]

  const handleSiteChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedSiteId(event.target.value as number | '')
  }

  const onRefresh = useCallback(() => {
    dispatch(
      findAllQuestions({
        includeExams: true,
        ...(selectedSiteId ? { siteId: selectedSiteId } : {}),
      }),
    )
    dispatch(findAllSites())
  }, [dispatch, selectedSiteId])

  useEffect(() => {
    onRefresh()
    return () => {
      dispatch(removeSites())
    }
  }, [dispatch, onRefresh])

  const handleDelete = async (row: QuestionDto) => {
    await dispatch(deleteQuestion(row.id))
    setDeleteDialogOpen(false)
  }

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return
    dispatch(bulkDeleteQuestions(selectedIds))
  }

  return (
    <Container maxWidth={false} style={{ height: '100%', padding: '0px 30px' }}>
      <Grid item container style={{ height: '90vh', marginTop: '25px' }} direction="column">
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="question-site-filter-label">Site</InputLabel>
            <Select
              labelId="question-site-filter-label"
              label="Site"
              value={selectedSiteId}
              onChange={handleSiteChange}
            >
              <MenuItem value="">Tous les sites</MenuItem>
              {sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Paper
          elevation={1}
          sx={{
            flex: 1,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.200',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DataTable
            rows={questions}
            columns={columns}
            loading={loading}
            toolBarProps={{
              title: 'Questions',
              icon: 'fab fa-wpforms',
              onAdd: () => handleOpen(),
              onRefresh,
              onDelete: handleBulkDelete,
            }}
            detailPanelHeight={400}
            checkboxSelection
            onSelectionModelChange={(ids) => setSelectedIds(ids as number[])}
          />
        </Paper>
      </Grid>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          {selectedQuestion ? 'Edit Question' : 'Create Question'}
        </DialogTitle>
        <DialogContent>
          <QuestionForm row={selectedQuestion} onClose={handleClose} />
        </DialogContent>
      </Dialog>

      {selectedQuestion && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={() => handleDelete(selectedQuestion)}
          title="Confirmer la suppression"
          description={'Êtes-vous sûr de vouloir supprimer cette question ?'}
        />
      )}
    </Container>
  )
}
